import {
  mapTypeaheadLocation,
  mapTypeaheadSuggestionToSearchItemV2,
} from 'api/mappers/Search/typeaheadStreamingMap'
import type {
  ResponseEvents,
  TypeaheadAmenitiesResponse,
  TypeaheadBedroomsResponse,
  TypeaheadChainsResponse,
  TypeaheadInclusionGapResponse,
  TypeaheadInclusionsResponse,
  TypeaheadIsQueryWellSupportedResponse,
  TypeaheadLocationResponse,
  TypeaheadNaturalLanguageSuggestionsV2Response,
  TypeaheadOccupancyResponse,
  TypeaheadPriceResponse,
  TypeaheadPropertyTypesResponse,
  TypeaheadPropertyResponseV2,
  TypeaheadShouldSemanticRerankResponse,
  TypeaheadSortByResponse,
  TypeaheadTagsResponse,
  TypeaheadTravelDatesResponse,
} from '@luxuryescapes/contract-search'

export interface TypeaheadSocketResponseBase {
  typeaheadId: string
  requestTimestamp: string
  input: string
}

export interface TypeaheadFieldSocketEvent {
  event: ResponseEvents
  extractedDataType: App.TypeaheadStreamingFieldType
  toUpdates: (response: TypeaheadSocketResponseBase) => App.TypeaheadStreamingUpdates
}

function fieldEvent<R extends TypeaheadSocketResponseBase>(
  event: ResponseEvents,
  extractedDataType: App.TypeaheadStreamingFieldType,
  toUpdates: (response: R) => App.TypeaheadStreamingUpdates,
): TypeaheadFieldSocketEvent {
  return { event, extractedDataType, toUpdates: toUpdates as TypeaheadFieldSocketEvent['toUpdates'] }
}

export const TYPEAHEAD_FIELD_SOCKET_EVENTS: Array<TypeaheadFieldSocketEvent> = [
  fieldEvent<TypeaheadNaturalLanguageSuggestionsV2Response>(
    'typeahead:response:suggestions:v2',
    'naturalLanguageSuggestions',
    (r) => ({ naturalLanguageSuggestions: r.suggestions.map(mapTypeaheadSuggestionToSearchItemV2) }),
  ),
  fieldEvent<TypeaheadLocationResponse>(
    'typeahead:response:location',
    'location',
    (r) => ({ location: mapTypeaheadLocation(r.location) }),
  ),
  fieldEvent<TypeaheadPropertyResponseV2>(
    'typeahead:response:property:v2',
    'property',
    (r) => {
      const { propertyId, propertyType } = r.property ?? {}
      return { property: propertyId && propertyType ? { propertyId, propertyType } : undefined }
    },
  ),
  fieldEvent<TypeaheadBedroomsResponse>(
    'typeahead:response:bedrooms',
    'bedrooms',
    (r) => ({ bedrooms: r.bedrooms }),
  ),
  fieldEvent<TypeaheadAmenitiesResponse>(
    'typeahead:response:amenities',
    'amenities',
    (r) => ({ amenities: r.amenities }),
  ),
  fieldEvent<TypeaheadPropertyTypesResponse>(
    'typeahead:response:property-types',
    'propertyTypes',
    (r) => ({ propertyTypes: r.propertyTypes }),
  ),
  fieldEvent<TypeaheadInclusionsResponse>(
    'typeahead:response:inclusions',
    'inclusions',
    (r) => ({ inclusions: r.inclusions }),
  ),
  fieldEvent<TypeaheadInclusionGapResponse>(
    'typeahead:response:inclusion-gap',
    'inclusionGap',
    (r) => ({ inclusionGap: r.inclusionGap }),
  ),
  fieldEvent<TypeaheadShouldSemanticRerankResponse>(
    'typeahead:response:should-semantic-rerank',
    'shouldSemanticRerank',
    (r) => ({ shouldSemanticRerank: r.shouldSemanticRerank }),
  ),
  fieldEvent<TypeaheadIsQueryWellSupportedResponse>(
    'typeahead:response:is-query-well-supported',
    'isQueryWellSupported',
    (r) => ({ isQueryWellSupported: r.isQueryWellSupported }),
  ),
  fieldEvent<TypeaheadTravelDatesResponse>(
    'typeahead:response:travel-dates',
    'travelDates',
    (r) => ({ travelDates: r.dates }),
  ),
  fieldEvent<TypeaheadOccupancyResponse>(
    'typeahead:response:occupancy',
    'occupancy',
    (r) => ({ occupancy: r.occupancy }),
  ),
  fieldEvent<TypeaheadPriceResponse>(
    'typeahead:response:price',
    'price',
    (r) => ({ price: r.price }),
  ),
  fieldEvent<TypeaheadTagsResponse>(
    'typeahead:response:tags',
    'tags',
    (r) => ({ tags: r.tags }),
  ),
  fieldEvent<TypeaheadChainsResponse>(
    'typeahead:response:chains',
    'chains',
    (r) => ({ chains: r.chains, subChains: r.subChains }),
  ),
  fieldEvent<TypeaheadSortByResponse>(
    'typeahead:response:sort-by',
    'sortBy',
    (r) => ({ sortBy: r.sortBy }),
  ),
]
